import React, { useEffect, useContext, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode } from '@craftjs/core';

import SelectColor from '@resolvers/tools/SelectColor/SelectColor';

import MyButton from '@common/components/Button/MyButton';
import {
  EDITOR_RESOLVERS,
  INIT_GRID_CELL_MODAL_STATE,
} from '@common/constants';

import ConditionalModal from './ConditionalModal';
import style from './GridCell.module.scss';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

const GridCell = ({
  cellWidth,
  conditionalFieldName,
  children,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  background,
  borderWidth,
  borderLeft,
  borderRight,
  borderTop,
  borderBottom,
  height,
  ifConditionalTrueHideElement,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    setCustom((props) => {
      props.displayName = t('grid_cell');
    }, 500);
  }, [setCustom, t]);

  return ifConditionalTrueHideElement ? (
    <div
      ref={ref}
      className={style.border}
      data-th-if={`${
        !!conditionalFieldName ? '${' + conditionalFieldName + '}' : 'true'
      } `}
      style={{
        display: 'block',
        minHeight: `${height}px`,
        backgroundColor: background,
        width: `${cellWidth}%`,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        borderLeft: borderLeft ? `${borderWidth}px solid #000000` : 'none',
        borderRight: borderRight ? `${borderWidth}px solid #000000` : 'none',
        borderTop: borderTop ? `${borderWidth}px solid #000000` : 'none',
        borderBottom: borderBottom ? `${borderWidth}px solid #000` : 'none',
        boxSizing: 'border-box',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
      }}
    >
      {children}
    </div>
  ) : (
    <div
      ref={ref}
      className={style.border}
      data-th-switch={'${' + conditionalFieldName + '}'}
      style={{
        display: 'block',
        minHeight: `${height}px`,
        backgroundColor: background,
        width: `${cellWidth}%`,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        borderLeft: borderLeft ? `${borderWidth}px solid #000000` : 'none',
        borderRight: borderRight ? `${borderWidth}px solid #000000` : 'none',
        borderTop: borderTop ? `${borderWidth}px solid #000000` : 'none',
        borderBottom: borderBottom ? `${borderWidth}px solid #000` : 'none',
        boxSizing: 'border-box',
      }}
    >
      <span data-th-case='false'></span>
      <span data-th-case='*'>{children}</span>
    </div>
  );
};

const GridCellConfigPanel = () => {
  const { source } = useTemplateDetailsStore();
  const [showModal, setShowModal] = useState(INIT_GRID_CELL_MODAL_STATE);

  const {
    actions: { setProp },
    conditionalFieldName,
    ifConditionalTrueHideElement,
  } = useNode(({ data: { props } }) => {
    if (!props) return {};
    return {
      elementProps: props.elementProps,
      conditionalFieldName: props.conditionalFieldName,
      ifConditionalTrueHideElement: props.ifConditionalTrueHideElement,
    };
  });

  const { t } = useTranslation();

  const changeText = (newValue) => {
    if (newValue === '') {
      newValue = true;
    }

    setProp((props) => {
      props.conditionalFieldName = newValue;
    }, 500);

    setShowModal(INIT_GRID_CELL_MODAL_STATE);
  };

  const onConditionsClick = (e, data) => {
    setShowModal({
      show: true,
      listSource: '',
      parent: '',
      showRootFields: typeof listSrc === 'undefined',
      action: 'source',
    });
  };

  return (
    <SettingsWrapper>
      <ConditionalModal
        currentValue={conditionalFieldName}
        changeTextFunc={changeText}
        documentJson={source?.structure || {}}
        showModal={showModal}
        setShowModal={setShowModal}
        setProp={setProp}
        ifConditionalTrueHideElement={ifConditionalTrueHideElement}
      />

      <SettingsRow sx={{ flexDirection: 'column', mb: 3 }}>
        <label>{t('select_condition')}</label>
        <MyButton name='source' onClick={onConditionsClick}>
          {t('condition_fields')}
        </MyButton>
      </SettingsRow>
      <SettingsRow>
        <SelectColor />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const GridCellDefaultProps = {
  children: null,
  conditionalFieldName: true,
  paddingLeft: '10',
  paddingRight: '10',
  paddingTop: '10',
  paddingBottom: '10',
  background: 'transparent',
  cellWidth: '25',
  borderWidth: '1',
  borderLeft: false,
  borderBottom: false,
  borderTop: false,
  borderRight: false,
  height: '40',
  ifConditionalTrueHideElement: false,
};

GridCell.defaultProps = GridCellDefaultProps;

GridCell.propTypes = {
  children: PropTypes.node,
  conditionalFieldName: PropTypes.string,
  size: PropTypes.number,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  background: PropTypes.string,
  cellWidth: PropTypes.string,
  borderWidth: PropTypes.string,
  borderLeft: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderTop: PropTypes.bool,
  borderRight: PropTypes.bool,
  height: PropTypes.string,
  ifConditionalTrueHideElement: PropTypes.bool,
};

GridCell.craft = {
  // props: GridCellDefaultProps,
  displayName: EDITOR_RESOLVERS.PDF.GRID_CELL,
  name: EDITOR_RESOLVERS.PDF.GRID_CELL,
  related: {
    settings: GridCellConfigPanel,
  },
};

export default GridCell;
